/**
 * What the track page can show, and the rule that maps a `?tab=` value —
 * spelled the way links across the app and TrackDetail spell it — onto it.
 *
 * Since 2026-11-03 the page's navigation is its progress strip: each step of
 * the track's work opens that step, and only Relationships, Lineage and
 * Discussion remain tabs beside it. Old tab names still arrive in links, so
 * each one lands on the step that now holds what the tab held.
 */
import { STORY_STOP_IDS, type StoryStopId } from './trackStory';

export type TrackStep = StoryStopId;
export type TrackSideTab = 'relationships' | 'lineage' | 'discussion';
export type TrackView = TrackStep | TrackSideTab;

export const TRACK_SIDE_TABS: TrackSideTab[] = ['relationships', 'lineage', 'discussion'];

/** `current`: the step the one thing to do now points at, worked out once the facts are read. */
export type TrackViewTarget = TrackView | 'current';

/** Tab names from before the strip, and TrackDetail's spellings, onto what holds them now. */
const URL_TAB_ALIASES: Record<string, TrackViewTarget> = {
  overview: 'current',
  readiness: 'current',
  participation: 'participants',
  // The old Defaults tab held the item scope a new track sets next, the owner,
  // and the session defaults; links to it come from "create a track".
  defaults: 'pool',
  'value-ranges': 'scales',
  results: 'published',
  'results-panel': 'published',
  provenance: 'lineage',
};

/** What a `?tab=` value names, or null when it names nothing on the page. */
export function trackViewFromUrl(tab: string | null): TrackViewTarget | null {
  if (!tab) return null;
  const aliased = URL_TAB_ALIASES[tab] ?? tab;
  if (aliased === 'current') return 'current';
  if ((STORY_STOP_IDS as readonly string[]).includes(aliased)) return aliased as TrackStep;
  if ((TRACK_SIDE_TABS as string[]).includes(aliased)) return aliased as TrackSideTab;
  return null;
}
